import React from 'react';
import PropTypes from 'prop-types';
import FaceIcon from '@mui/icons-material/Face';
import { useTheme } from '@mui/material/styles';
import {
    StatusContainer,
    StatusIndicator,
    StatusText,
    StatusIcon,
    ClockIconWrapper,
    FaceIconWrapper
} from './EmployeeStatus.styles';

// Import SVG files directly as React components
import { ReactComponent as BlueClockSvg } from '../../../../assets/icons/blueClock.svg';
import { ReactComponent as LightBlueClockSvg } from '../../../../assets/icons/lightBlueClock.svg';
import { ReactComponent as YellowClockSvg } from '../../../../assets/icons/yellowClock.svg';
import { ReactComponent as GreenClockSvg } from '../../../../assets/icons/greenClock.svg';
import { ReactComponent as RedExclaimationSvg } from '../../../../assets/icons/redExclaimation.svg';
import { ReactComponent as OrangeTriangleSvg } from '../../../../assets/icons/orangeTriangle.svg';

// Enhanced clock icons with better visual appeal
const ClockIcon = ({ SvgComponent, size = 16, color, glowColor }) => (
    <ClockIconWrapper color={color} glowColor={glowColor}>
        <SvgComponent width={size} height={size} />
    </ClockIconWrapper>
);

const EmployeeStatus = ({ status, primaryStatus, statusMessage, breakStatus }) => {
    const theme = useTheme();

    // Status configuration mapping with enhanced icons
    const statusConfig = {
        'clocked-in': {
            color: 'success',
            icon: <ClockIcon SvgComponent={GreenClockSvg} color={theme.colors.status.clockedIn} glowColor={theme.colors.status.clockedIn} />,
            text: 'Clocked in'
        },
        'break-completed': {
            color: 'success',
            icon: <ClockIcon SvgComponent={GreenClockSvg} color={theme.colors.status.clockedIn} glowColor={theme.colors.status.clockedIn} />,
            text: 'Clocked in - Break completed'
        },
        'on-break': {
            color: 'info',
            icon: <ClockIcon SvgComponent={BlueClockSvg} color={theme.colors.status.onBreak} glowColor={theme.colors.status.onBreak} />,
            text: 'On break'
        },
        'break-due': {
            color: 'warning',
            icon: <ClockIcon SvgComponent={YellowClockSvg} color={theme.colors.status.breakDue} glowColor={theme.colors.status.breakDue} />,
            text: statusMessage || 'Break due'
        },
        'break-overdue': {
            color: 'error',
            icon: <ClockIcon SvgComponent={OrangeTriangleSvg} color={theme.colors.status.breakOverdue} glowColor={theme.colors.status.breakOverdue} />,
            text: getBreakOverdueText(breakStatus)
        },
        'late': {
            color: 'error',
            icon: <ClockIcon SvgComponent={RedExclaimationSvg} color={theme.colors.status.late} glowColor={theme.colors.status.late} />,
            text: statusMessage || 'Late'
        },
        'upcoming': {
            color: 'info',
            icon: <ClockIcon SvgComponent={LightBlueClockSvg} color={theme.colors.status.upcoming} glowColor={theme.colors.status.upcoming} />,
            text: breakStatus?.type === 'upcoming' ? (statusMessage || 'Upcoming break') : (statusMessage || 'Upcoming shift')
        },
        'missed': {
            color: 'error',
            icon: <ClockIcon SvgComponent={RedExclaimationSvg} color={theme.colors.status.late} glowColor={theme.colors.status.late} />,
            text: statusMessage || 'Shift missed'
        },
        'clocked-out': {
            color: 'neutral',
            icon: <FaceIconWrapper>
                <FaceIcon sx={{
                    fontSize: 16,
                    color: theme.colors.status.clockedOut,
                    filter: 'drop-shadow(0 0 2px rgba(142,142,147,0.3))'
                }} />
            </FaceIconWrapper>,
            text: 'Off duty'
        }
    };

    const displayStatus = primaryStatus || status;
    const config = statusConfig[displayStatus] || statusConfig['clocked-out'];

    return (
        <StatusContainer>
            <StatusIndicator>
                <StatusIcon>{config.icon}</StatusIcon>
                <StatusText status={displayStatus}>
                    {config.text}
                </StatusText>
            </StatusIndicator>
        </StatusContainer>
    );
};

// Helper function for break overdue text
const getBreakOverdueText = (breakStatus) => {
    if (!breakStatus?.breakName) return 'Break overdue';

    const breakName = breakStatus.breakName.trim().toLowerCase();
    if (breakName === 'rest' || breakName === 'rest break') return 'Rest break overdue';
    if (breakName === 'meal' || breakName === 'meal break') return 'Meal break overdue';
    return 'Break overdue';
};

EmployeeStatus.propTypes = {
    status: PropTypes.oneOf([
        'clocked-in',
        'clocked-out',
        'on-break',
        'late',
        'upcoming',
        'break-due',
        'break-overdue',
        'break-completed',
        'missed'
    ]).isRequired,
    primaryStatus: PropTypes.oneOf([
        'clocked-in',
        'clocked-out',
        'on-break',
        'late',
        'upcoming',
        'break-due',
        'break-overdue',
        'break-completed',
        'missed'
    ]),
    statusMessage: PropTypes.string,
    breakStatus: PropTypes.shape({
        type: PropTypes.oneOf(['upcoming', 'due', 'overdue', 'active', 'completed', 'missed']),
        breakName: PropTypes.string,
        timeInfo: PropTypes.string
    })
};

export default EmployeeStatus;
